import { useContext, useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DataContext } from "../../context/DataContext";
import { callPostApi } from "../../function/ApiCallFunction";
import { consoleColors } from "../admin/consoleTheme";
import { formatAmount } from "../../include/money";
import GradientLoader from "../../components/CommanComponents/GradientLoader";

export default function CustomerHistory() {
  const navigate = useNavigate();
  const { customerId = "" } = useParams();
  const { loginData } = useContext(DataContext);
  const storeId = loginData?.store_info?.store_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!storeId || !customerId) return undefined;
    let mounted = true;
    setLoading(true);
    callPostApi("cashi-customer", {
      mod: "CASHI_CUSTOMER_HISTORY",
      data_arr: { store_id: storeId, customer_id: customerId },
    }).then((result) => {
      if (!mounted) return;
      if (result.status === "200") setData(result.data || null);
      else toast.error(result.status_message || result.data?.error || "Could not load customer history");
    }).finally(() => {
      if (mounted) setLoading(false);
    });
    return () => { mounted = false; };
  }, [storeId, customerId]);

  const customer = data?.customer || {};
  const totals = data?.totals || {};

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button variant="contained" sx={{ background: "#1976d2", mb: 2, textTransform: "none" }} onClick={() => navigate("/customerList")}>
        Back to customers
      </Button>

      {loading && <GradientLoader text="Loading" />}

      {!loading && data && (
        <>
          <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2.5, mb: 2 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 750 }}>{customer.customer_name || "Customer"}</Typography>
            <Typography sx={{ fontSize: 14, color: consoleColors.muted, mt: 0.4 }}>
              {[customer.customer_mobile, customer.customer_email, customer.cust_type].filter(Boolean).join(" · ")}
            </Typography>
            {customer.club_title ? (
              <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.5 }}>
                Club pool {customer.club_title}: {customer.available_points} available
                {customer.others?.length
                  ? ` · ${customer.others.map((row) => `${row.points} from ${row.store_name}`).join(" · ")}`
                  : ""}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.5, mb: 2 }}>
            <Stat label="Visits" value={totals.visits || 0} />
            <Stat label="Spend" value={`₹${formatAmount(totals.spend)}`} />
            <Stat label="Loyalty now" value={customer.loyalty_point || 0} />
            <Stat label="Points earned" value={totals.earned || 0} />
            <Stat label="Points used" value={totals.redeemed || 0} />
          </Box>

          <Section title="Purchases">
            {(data.sales || []).length === 0 ? <Empty text="No purchases yet." /> : (data.sales || []).map((row) => (
              <Row key={row.sale_id} title={`₹${formatAmount(row.order_amount)} · ${row.payment_type || "SALE"}`} detail={`Earned ${row.points_earned} · Used ${row.points_redeemed}`} date={row.date_added} />
            ))}
          </Section>

          <Section title="Loyalty used">
            {(data.loyalty_uses || []).length === 0 ? <Empty text="No loyalty points used yet." /> : (data.loyalty_uses || []).map((row) => (
              <Row key={row.use_id} title={`${row.points} points at ${row.store_name || "store"}`} detail={row.source_text || ""} date={row.date_added} />
            ))}
          </Section>

          <Section title="Coupons">
            {(data.coupons || []).length === 0 ? <Empty text="No coupons issued." /> : (data.coupons || []).map((row) => (
              <Row key={row.coupon_id || row.coupon_code} title={row.offer_title || row.coupon_code} detail={`${row.coupon_code} · ${row.status}`} date={row.genereate_date || row.coupon_expire} />
            ))}
          </Section>

          <Section title="Lucky Draw Coupons">
            {(data.lucky || []).length === 0 ? <Empty text="No lucky-draw coupons." /> : (data.lucky || []).map((row) => (
              <Row key={row.coupon_code} title={row.coupon_code} detail={row.is_winner ? "Winner" : `Bill ₹${row.order_amount || "0"}`} date={row.date_added} />
            ))}
          </Section>

          {(data.qrs || []).length > 0 && (
            <Section title="QR redeems">
              {data.qrs.map((row) => (
                <Row key={row.qrcode_number} title={row.qrcode_number} detail={`₹${row.amount}`} date={row.redeem_date} />
              ))}
            </Section>
          )}

          {(data.cards || []).length > 0 && (
            <Section title="Physical cards">
              {data.cards.map((row) => (
                <Row key={row.card_token} title={row.card_type_name || "Card"} detail={row.card_token} date={row.date_added} />
              ))}
            </Section>
          )}

          {(data.reviews || []).length > 0 && (
            <Section title="Reviews">
              {data.reviews.map((row, index) => (
                <Row key={`${row.date_added}-${index}`} title={`Shopping ${row.shopping_rating} · Product ${row.product_rating} · Staff ${row.staff_rating}`} detail={row.comment} date={row.date_added} />
              ))}
            </Section>
          )}
        </>
      )}
    </Box>
  );
}

function Stat({ label, value }) {
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 1.8 }}>
      <Typography sx={{ fontSize: 12, color: consoleColors.muted, fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 750, mt: 0.3 }}>{value}</Typography>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ background: "#fff", border: `1px solid ${consoleColors.line}`, borderRadius: 2.5, p: 2, mb: 1.5 }}>
      <Typography sx={{ fontSize: 16, fontWeight: 750, mb: 1 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function Row({ title, detail, date }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1.5, py: 0.9, borderTop: `1px solid ${consoleColors.line}`, flexWrap: "wrap" }}>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 650 }}>{title}</Typography>
        {detail ? <Typography sx={{ fontSize: 13, color: consoleColors.muted, mt: 0.2 }}>{detail}</Typography> : null}
      </Box>
      <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{date || ""}</Typography>
    </Box>
  );
}

function Empty({ text }) {
  return <Typography sx={{ fontSize: 13, color: consoleColors.muted }}>{text}</Typography>;
}
